import React, { useCallback, useMemo, useState } from 'react';
import { type AutosaveState, AutosaveStatus } from '@components/AutosaveStatus';
import AddIcon from '@mui/icons-material/Add';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import { Box, Button, Chip, IconButton, useTheme } from '@mui/material';
import {
  DataGrid,
  type GridColDef,
  type GridColumnVisibilityModel,
  type GridDensity,
  type GridRowId,
  type GridRowModel,
  GridToolbarColumnsButton,
  GridToolbarContainer,
  GridToolbarDensitySelector,
  useGridApiRef,
} from '@mui/x-data-grid';
import { useEventTracking } from '@services/statsig';

import type { Allergy } from './PatientAllergiesCard';

interface PatientAllergiesEditCardProps {
  allergies: Allergy[];
  onAllergiesChange: (allergies: Allergy[]) => void;
  isLoading?: boolean;
  patientId?: string;
  /** Optional autosave status to display in toolbar */
  autosaveStatus?: AutosaveState;
  /** Whether there are unsaved changes (for autosave indicator) */
  isDirty?: boolean;
  /** Last saved timestamp (for autosave indicator) */
  lastSavedAt?: Date | null;
  /** Source identifier for analytics */
  source?: string;
}

// Custom toolbar with column selector and add button
interface CustomToolbarProps {
  onAddRow: () => void;
  isLoading: boolean;
  buttonLabel: string;
  autosaveStatus?: AutosaveState;
  isDirty?: boolean;
  lastSavedAt?: Date | null;
}

const CustomToolbar: React.FC<CustomToolbarProps> = ({
  onAddRow,
  isLoading,
  buttonLabel,
  autosaveStatus,
  isDirty,
  lastSavedAt,
}) => {
  return (
    <GridToolbarContainer
      sx={{
        mb: 3,
        gap: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        '& .MuiButton-root': {
          borderRadius: '20px',
          textTransform: 'none',
          fontWeight: 500,
        },
      }}
    >
      <Box display="flex" alignItems="center" gap={1}>
        <GridToolbarColumnsButton />
        <GridToolbarDensitySelector />
      </Box>
      <Box display="flex" alignItems="center" gap={2}>
        {autosaveStatus !== undefined && (
          <AutosaveStatus
            status={autosaveStatus}
            isDirty={isDirty ?? false}
            lastSavedAt={lastSavedAt}
          />
        )}
        <Button
          variant="contained"
          size="small"
          startIcon={<AddIcon />}
          onClick={onAddRow}
          disabled={isLoading}
          sx={{
            borderRadius: '999px',
            textTransform: 'none',
            fontWeight: 500,
            px: 3,
            bgcolor: 'primary.main',
            '&:hover': {
              bgcolor: 'primary.dark',
            },
            '&:disabled': {
              bgcolor: 'action.disabledBackground',
              color: 'action.disabled',
            },
          }}
        >
          {buttonLabel}
        </Button>
      </Box>
    </GridToolbarContainer>
  );
};

const formatDate = (date: Date | string | null | undefined): string => {
  if (!date) return '';
  if (typeof date === 'string') return date.split('T')[0];
  return date.toISOString().split('T')[0];
};

// Fields that should always be visible regardless of data
const ALWAYS_VISIBLE_ALLERGY_FIELDS = ['allergy_name', 'isActive', 'actions'];

export const PatientAllergiesEditCard: React.FC<PatientAllergiesEditCardProps> = ({
  allergies,
  onAllergiesChange,
  isLoading = false,
  patientId,
  autosaveStatus,
  isDirty,
  lastSavedAt,
  source = 'context_bar',
}) => {
  const theme = useTheme();
  const [density, setDensity] = useState<GridDensity>('compact');
  const apiRef = useGridApiRef();
  const { patient: patientTracking } = useEventTracking();

  // Calculate initial column visibility based on data (hide empty columns)
  const initialColumnVisibilityModel = useMemo(() => {
    const visibilityModel: GridColumnVisibilityModel = {};
    const fields = [
      'allergy_name',
      'reaction',
      'severity',
      'start_date',
      'end_date',
      'isActive',
      'actions',
    ];

    fields.forEach(field => {
      // Always show essential fields
      if (ALWAYS_VISIBLE_ALLERGY_FIELDS.includes(field)) {
        visibilityModel[field] = true;
        return;
      }

      // When there's no data, show all columns
      if (allergies.length === 0) {
        visibilityModel[field] = true;
        return;
      }

      // Only show columns that have at least one non-empty value
      const hasValue = allergies.some(allergy => {
        const value = allergy[field as keyof Allergy];
        return value !== null && value !== undefined && value !== '';
      });
      visibilityModel[field] = hasValue;
    });

    return visibilityModel;
  }, [allergies]);

  const [columnVisibilityModel, setColumnVisibilityModel] = useState<GridColumnVisibilityModel>(
    initialColumnVisibilityModel,
  );

  const handleDeleteClick = useCallback(
    (id: GridRowId) => {
      const updatedAllergies = allergies.filter((_, index) => index !== id);
      onAllergiesChange(updatedAllergies);
      if (patientId) {
        patientTracking.updated(patientId, {
          source,
          fields_updated: ['allergies_item_deleted'],
        });
      }
    },
    [allergies, onAllergiesChange, patientId, patientTracking, source],
  );

  const processRowUpdate = useCallback(
    (newRow: GridRowModel) => {
      const rowIndex = newRow.id as number;
      const isNew = (allergies[rowIndex] as any)?.isNew;
      // Keep the two name fields in sync so the popover/card always has a label.
      const nextName = (newRow.allergy_name as string | null | undefined) ?? null;
      const updatedRow = {
        ...newRow,
        name: nextName,
        allergy_name: nextName,
        isNew: false,
      } as unknown as Allergy;
      const updatedAllergies = allergies.map((allergy, index) =>
        index === rowIndex ? updatedRow : allergy,
      );
      onAllergiesChange(updatedAllergies);
      if (patientId) {
        patientTracking.updated(patientId, {
          source,
          fields_updated: [isNew ? 'allergies_item_added' : 'allergies_item_edited'],
        });
      }
      return { ...updatedRow, id: rowIndex };
    },
    [allergies, onAllergiesChange, patientId, patientTracking, source],
  );

  const handleAddRow = () => {
    const defaultName = `New Allergy ${allergies.length + 1}`;
    const newAllergy: Allergy = {
      name: defaultName,
      allergy_name: defaultName,
      reaction: null,
      severity: null,
      start_date: null,
      end_date: null,
      isActive: true,
    };
    const updatedAllergies = [...allergies, { ...newAllergy, isNew: true } as Allergy];
    onAllergiesChange(updatedAllergies);

    // Focus the first cell of the new row after a short delay
    const newIndex = allergies.length; // The index of the new row
    setTimeout(() => {
      const firstColumn = 'allergy_name';
      if (apiRef.current) {
        apiRef.current.startCellEditMode({
          id: newIndex,
          field: firstColumn,
        });
      }
    }, 100);

    if (patientId) {
      patientTracking.updated(patientId, {
        source: 'context_bar',
        fields_updated: ['allergies_add_button_clicked'],
      });
    }
  };

  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: 'allergy_name',
        headerName: 'Allergy',
        minWidth: 180,
        flex: 1.5,
        editable: true,
        valueGetter: (_, row) => row.allergy_name ?? row.name ?? '',
      },
      {
        field: 'reaction',
        headerName: 'Reaction',
        minWidth: 160,
        flex: 1.2,
        editable: true,
      },
      {
        field: 'severity',
        headerName: 'Severity',
        minWidth: 120,
        flex: 0.8,
        editable: true,
      },
      {
        field: 'start_date',
        headerName: 'Start Date',
        flex: 1,
        editable: true,
        type: 'date',
        valueGetter: (_, row) => {
          const value = row.start_date;
          return value ? new Date(value) : null;
        },
        valueFormatter: (_, row) => formatDate(row.start_date),
      },
      {
        field: 'end_date',
        headerName: 'End Date',
        flex: 1,
        editable: true,
        type: 'date',
        valueGetter: (_, row) => {
          const value = row.end_date;
          return value ? new Date(value) : null;
        },
        valueFormatter: (_, row) => formatDate(row.end_date),
      },
      {
        field: 'isActive',
        headerName: 'Status',
        flex: 1,
        editable: true,
        type: 'boolean',
        renderCell: params => {
          return (
            <Chip
              label={params.value ? 'Active' : 'Inactive'}
              size="small"
              sx={{
                borderRadius: '999px',
                bgcolor: params.value ? 'rgba(103, 80, 164, 0.12)' : 'rgba(121, 116, 126, 0.12)',
                color: params.value ? 'rgba(103, 80, 164, 1)' : 'rgba(121, 116, 126, 1)',
                fontWeight: 500,
                border: 'none',
              }}
            />
          );
        },
      },
      {
        field: 'actions',
        headerName: '',
        width: 50,
        sortable: false,
        filterable: false,
        editable: false,
        renderCell: params => (
          <IconButton
            size="small"
            aria-label="Delete allergy"
            onClick={e => {
              e.stopPropagation();
              handleDeleteClick(params.id);
            }}
            sx={{
              color: 'grey.500',
              '&:hover': {
                color: 'error.main',
              },
            }}
          >
            <RemoveCircleOutlineIcon fontSize="small" />
          </IconButton>
        ),
      },
    ],
    [handleDeleteClick],
  );

  const rows = useMemo(() => {
    return allergies.map((allergy, index) => ({ ...allergy, id: index }));
  }, [allergies]);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        backgroundColor: theme.palette.background.default,
      }}
    >
      <Box sx={{ flex: 1, minHeight: 0 }}>
        <DataGrid
          apiRef={apiRef}
          rows={rows}
          columns={columns}
          density={density}
          onDensityChange={newDensity => setDensity(newDensity)}
          columnVisibilityModel={columnVisibilityModel}
          onColumnVisibilityModelChange={setColumnVisibilityModel}
          disableRowSelectionOnClick
          processRowUpdate={processRowUpdate}
          hideFooter
          slots={{
            toolbar: () => (
              <CustomToolbar
                onAddRow={handleAddRow}
                isLoading={isLoading}
                buttonLabel="Add Allergy"
                autosaveStatus={autosaveStatus}
                isDirty={isDirty}
                lastSavedAt={lastSavedAt}
              />
            ),
          }}
          sx={{
            border: 'none',
            '& .MuiDataGrid-columnHeaders': {
              bgcolor: 'action.hover',
              borderRadius: '12px',
              fontWeight: 600,
              borderBottom: 'none',
            },
            '& .MuiDataGrid-row': {
              borderRadius: '8px',
              '&:hover': {
                bgcolor: 'action.hover',
              },
            },
            '& .MuiDataGrid-cell': {
              borderBottom: 'none',
            },
          }}
        />
      </Box>
    </Box>
  );
};
